import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BookOpen, TrendingUp, Users, Award, ArrowRight, CheckCircle, Gift, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import Footer from './Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogInsights = () => {
  // Lead magnet form state
  const [leadForm, setLeadForm] = useState({ name: '', email: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadForm.name || !leadForm.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setLeadSubmitting(true);
    try {
      const response = await fetch(`${API}/api/lead-magnet/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: leadForm.name,
          email: leadForm.email
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }
      
      setLeadSuccess(true);
      toast.success('Check your email! Your free guide is on its way.');
    } catch (error) {
      console.error('Lead magnet error:', error);
      toast.error('Failed to send guide. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  // Load Soro embed script
  useEffect(() => {
    const loadSoroEmbed = () => {
      const target = document.getElementById('soro-blog');
      if (target) {
        // Remove loading message
        const loading = document.getElementById('soro-loading');
        if (loading) loading.style.display = 'none';
        
        // Create and append script
        const script = document.createElement('script');
        script.async = true;
        const params = new URLSearchParams(window.location.search);
        let url = 'https://app.trysoro.com/api/embed/9103899f-4e87-4fcc-af0e-f754bc23e027';
        if (params.get('post')) {
          url += '?post=' + encodeURIComponent(params.get('post'));
        }
        script.src = url;
        target.appendChild(script);
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(loadSoroEmbed, 500);
  }, []);

  // Blog Page Schema
  const blogSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        "@id": "https://enrolment.bookreviewspecialists.app/blog-insights#blog",
        "name": "Video Book Review Insights & Income Strategies",
        "description": "Learn how to earn from video book reviews, why text reviews are declining, and how to start a new income stream with CPD accredited training.",
        "url": "https://enrolment.bookreviewspecialists.app/blog-insights",
        "publisher": {
          "@type": "Organization",
          "name": "Video Book Review Specialist",
          "url": "https://enrolment.bookreviewspecialists.app"
        }
      },
      {
        "@type": "WebPage",
        "name": "Video Book Review Blog | Earn from Book Reviews in 2026",
        "url": "https://enrolment.bookreviewspecialists.app/blog-insights",
        "description": "Learn how to earn from video book reviews, why text reviews are declining, and how to start a new income stream with CPD accredited training."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Video Book Review Blog | Earn from Book Reviews in 2026</title>
        <meta name="description" content="Learn how to earn from video book reviews, why text reviews are declining, and how to start a new income stream with CPD accredited training." />
        <link rel="canonical" href="https://enrolment.bookreviewspecialists.app/blog-insights" />
        <meta property="og:title" content="Video Book Review Blog | Earn from Book Reviews in 2026" />
        <meta property="og:description" content="Learn how to earn from video book reviews, why text reviews are declining, and how to start a new income stream with CPD accredited training." />
        <meta property="og:url" content="https://enrolment.bookreviewspecialists.app/blog-insights" />
        <meta property="og:type" content="blog" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Video Book Review Blog | Earn from Book Reviews in 2026" />
        <meta name="twitter:description" content="Learn how to earn from video book reviews, why text reviews are declining, and how to start a new income stream with CPD accredited training." />
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <a href="https://bookreviewspecialists.app" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                alt="Video Book Review Specialist Logo"
                className="w-10 h-10 rounded-full object-cover"
              />
              <span className="text-xl font-bold text-slate-800">Video Book Review Specialist</span>
            </a>
            <nav className="hidden md:flex items-center space-x-6">
              <a href="https://enrolment.bookreviewspecialists.app" className="text-slate-600 hover:text-slate-800">Home</a>
              <a href="https://enrolment.bookreviewspecialists.app/become-a-video-book-review-specialist" className="text-purple-600 hover:text-purple-700 font-medium">Become a Specialist</a>
              <a href="https://enrolment.bookreviewspecialists.app/book-video-reviews-for-authors" className="text-pink-600 hover:text-pink-700 font-medium">For Authors</a>
              <a href="https://enrolment.bookreviewspecialists.app/enroll" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Enrol Now</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section with H1 */}
      <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Video Book Review Insights & Income Strategies
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Discover how video book reviewing is creating new income opportunities for thousands of people worldwide. 
              Learn the strategies, trends, and techniques that are transforming the publishing industry.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://qualiquiz.app/quiz/what-is-the-most-realistic-way-for-you-to-build-income-right-now" className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                Take the 60-Second Assessment
              </a>
              <a href="https://enrolment.bookreviewspecialists.app/enroll" className="bg-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-600 transition-colors">
                Start Enrolment for £9.99
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Feed Section - MOVED TO TOP */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-800 mb-2 text-center">Latest Articles & Insights</h2>
            <p className="text-slate-600 text-center mb-8">Stay updated with the latest trends, tips, and strategies in video book reviewing</p>
            
            {/* Featured Video */}
            <div className="mb-10">
              <div className="relative w-full max-w-3xl mx-auto" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-xl shadow-lg"
                  src="https://www.youtube.com/embed/sORMSZ5fumg?si=hUk80shSYa383_KM"
                  title="Video Book Review Specialists"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            
            {/* Soro Blog Embed */}
            <div id="soro-blog" className="min-h-[500px] bg-white rounded-xl shadow-sm p-4">
              {/* Loading state */}
              <div className="text-center py-8 text-slate-500" id="soro-loading">
                Loading articles...
              </div>
              {/* Fallback content for SEO - visible before JS loads */}
              <noscript>
                <div className="bg-white rounded-xl p-8 text-center">
                  <p className="text-slate-600">Please enable JavaScript to view our latest blog posts.</p>
                  <p className="text-slate-500 mt-2">Or visit our main site at <a href="https://bookreviewspecialists.app" className="text-blue-600 hover:underline">bookreviewspecialists.app</a></p>
                </div>
              </noscript>
            </div>
          </div>
        </div>
      </section>

      {/* Main Static Content - SEO Optimized */}
      <article className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto prose prose-lg">
          
          {/* Introduction Section */}
          <section className="mb-12">
            <p className="text-xl text-slate-700 leading-relaxed mb-6">
              The world of book promotion is changing rapidly. Traditional text-based book reviews, once the cornerstone of book marketing, 
              are being overshadowed by dynamic, engaging video content. Video book reviews have emerged as one of the most powerful tools 
              for authors to reach new readers and for reviewers to build profitable side incomes.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Whether you're looking to start a flexible home-based business, supplement your existing income, or help authors promote 
              their work, video book reviewing offers a unique opportunity in today's digital economy. With the rise of platforms like 
              YouTube, TikTok, and Instagram, the demand for professional video book reviews has never been higher.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Best of all, you don't need to read entire books, appear on camera, or have technical video production skills. 
              Modern AI-assisted tools and proven frameworks make it possible for anyone to create professional-quality video book reviews 
              and start earning within weeks of training.
            </p>
          </section>

          {/* Section 1: Why Video Reviews */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-6 flex items-center">
              <TrendingUp className="w-8 h-8 text-blue-600 mr-3" />
              Why Video Book Reviews Are Replacing Text Reviews
            </h2>
            <p className="text-lg text-slate-600 mb-4">
              The statistics speak for themselves: video content generates 1200% more shares than text and images combined. 
              Readers are increasingly turning to video platforms to discover their next book, and authors are recognizing 
              that traditional text reviews no longer capture attention in a crowded marketplace.
            </p>
            <ul className="space-y-3 text-slate-600 mb-6">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                <span><strong>Higher engagement rates:</strong> Video reviews keep viewers watching for longer and create stronger emotional connections</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                <span><strong>Better conversion:</strong> Authors report significantly higher book sales from video reviews compared to written ones</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                <span><strong>Platform algorithms favor video:</strong> YouTube, TikTok, and social media algorithms prioritize video content</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                <span><strong>Evergreen content:</strong> Video reviews continue generating views and sales for years after publication</span>
              </li>
            </ul>
            <p className="text-lg text-slate-600">
              Learn more about how video reviews are transforming book marketing on our 
              <a href="https://enrolment.bookreviewspecialists.app/book-video-reviews-for-authors" className="text-blue-600 hover:underline ml-1">services for authors page</a>.
            </p>
          </section>

          {/* Section 2: How to Start Earning */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-6 flex items-center">
              <Award className="w-8 h-8 text-purple-600 mr-3" />
              How You Can Start Earning from Book Reviews
            </h2>
            <p className="text-lg text-slate-600 mb-4">
              The path to becoming a paid video book reviewer is more accessible than ever. With 
              <a href="https://enrolment.bookreviewspecialists.app/become-a-video-book-review-specialist" className="text-purple-600 hover:underline mx-1">CPD accredited training</a>, 
              you can learn the skills needed to create professional video book reviews and start offering your services to authors worldwide.
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-semibold text-slate-800 mb-4">What You'll Learn:</h3>
              <ul className="space-y-2 text-slate-600">
                <li>• How to create engaging video book reviews using AI-assisted tools</li>
                <li>• Professional video frameworks that work across all platforms</li>
                <li>• How to find and work with authors seeking video promotion</li>
                <li>• Pricing strategies and building your client base</li>
                <li>• Creating book trailers and chapter excerpts</li>
              </ul>
            </div>
            <p className="text-lg text-slate-600">
              Enrolment starts from just £9.99. 
              <a href="https://enrolment.bookreviewspecialists.app/enroll" className="text-emerald-600 hover:underline font-semibold ml-1">Start your journey today</a> 
              and join thousands of others building income from video book reviews.
            </p>
          </section>

          {/* Section 3: Who Is This For */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-6 flex items-center">
              <Users className="w-8 h-8 text-emerald-600 mr-3" />
              Who This Opportunity Is For
            </h2>
            <p className="text-lg text-slate-600 mb-6">
              Video book reviewing isn't just for professional reviewers or tech experts. This opportunity is perfect for:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Beginners & Career Changers</h3>
                <p className="text-slate-600">No prior experience needed. Learn everything from scratch with our structured training program.</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Side Hustlers</h3>
                <p className="text-slate-600">Work flexible hours around your existing commitments. Create videos when it suits you.</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Existing Reviewers</h3>
                <p className="text-slate-600">Upgrade your skills and start earning more from your reviews with video content.</p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Authors & Publishers</h3>
                <p className="text-slate-600">Learn to create your own promotional videos or understand what makes effective book marketing.</p>
              </div>
            </div>
            <p className="text-lg text-slate-600">
              Not sure if this is right for you? 
              <a href="https://qualiquiz.app/quiz/what-is-the-most-realistic-way-for-you-to-build-income-right-now" className="text-blue-600 hover:underline ml-1">Take our quick assessment</a> 
              to find out if video book reviewing matches your goals and circumstances.
            </p>
          </section>

        </div>
      </article>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-emerald-500 to-teal-500 py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Earning from Video Book Reviews?
          </h2>
          <p className="text-white/90 text-lg mb-6 max-w-2xl mx-auto">
            Join our CPD accredited training program and learn how to create professional video book reviews from home.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://enrolment.bookreviewspecialists.app/enroll" 
              className="bg-white text-emerald-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-50 transition-colors shadow-lg"
            >
              Enrol Now from £9.99
            </a>
            <a 
              href="https://qualiquiz.app/quiz/join-the-official-waiting-list-train-as-a-cpd-video-book-review-specialist" 
              className="bg-emerald-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-800 transition-colors"
            >
              Join the Waiting List
            </a>
          </div>
        </div>
      </section>

      {/* Explore More Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">Explore More</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <a href="https://enrolment.bookreviewspecialists.app/become-a-video-book-review-specialist" className="block bg-purple-50 rounded-xl p-6 hover:bg-purple-100 transition-colors">
                <BookOpen className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="font-semibold text-slate-800 mb-2">Training Program</h3>
                <p className="text-slate-600 text-sm">Learn about our CPD accredited training course</p>
              </a>
              <a href="https://enrolment.bookreviewspecialists.app/book-video-reviews-for-authors" className="block bg-pink-50 rounded-xl p-6 hover:bg-pink-100 transition-colors">
                <TrendingUp className="w-8 h-8 text-pink-600 mb-3" />
                <h3 className="font-semibold text-slate-800 mb-2">Services for Authors</h3>
                <p className="text-slate-600 text-sm">Get professional video reviews for your book</p>
              </a>
              <a href="https://enrolment.bookreviewspecialists.app/#testimonials" className="block bg-blue-50 rounded-xl p-6 hover:bg-blue-100 transition-colors">
                <Users className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-semibold text-slate-800 mb-2">Success Stories</h3>
                <p className="text-slate-600 text-sm">Read testimonials from our graduates</p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Free Guide Lead Magnet Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left side - Image */}
              <div className="order-2 lg:order-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/81vacddd_IMG_20260401_203709.jpg"
                  alt="Mini Guide - Find Buyers Not Just Followers"
                  className="w-full rounded-xl shadow-2xl"
                />
              </div>
              
              {/* Right side - Form */}
              <div className="order-1 lg:order-2">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="flex items-center mb-4">
                    <Gift className="w-8 h-8 text-yellow-400 mr-3" />
                    <span className="text-yellow-400 font-semibold text-lg">FREE GUIDE</span>
                  </div>
                  
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    How to Profile, Find Buyers & Convert Followers into Serious Buyers
                  </h2>
                  
                  <p className="text-blue-100 mb-6">
                    Learn why most people focus on followers instead of buyers — and get practical ChatGPT prompts to identify high-intent buyers.
                  </p>
                  
                  {!leadSuccess ? (
                    <form onSubmit={handleLeadSubmit} className="space-y-4">
                      <div>
                        <Input
                          type="text"
                          placeholder="Your Name"
                          value={leadForm.name}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-white/90 border-0 text-slate-800 placeholder:text-slate-500"
                          disabled={leadSubmitting}
                        />
                      </div>
                      <div>
                        <Input
                          type="email"
                          placeholder="Your Email"
                          value={leadForm.email}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, email: e.target.value }))}
                          className="bg-white/90 border-0 text-slate-800 placeholder:text-slate-500"
                          disabled={leadSubmitting}
                        />
                      </div>
                      <Button 
                        type="submit" 
                        disabled={leadSubmitting}
                        className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-3 text-lg"
                      >
                        {leadSubmitting ? (
                          <div className="flex items-center justify-center">
                            <div className="spinner mr-2"></div>
                            Sending...
                          </div>
                        ) : (
                          <>
                            <Download className="w-5 h-5 mr-2" />
                            Get Free Guide Now
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-blue-200 text-center mt-3">
                        We respect your privacy. Unsubscribe anytime.
                      </p>
                    </form>
                  ) : (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Check Your Email!</h3>
                      <p className="text-blue-100">
                        Your free guide is on its way to <strong>{leadForm.email}</strong>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-slate-900 py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Start Your Video Book Review Journey Today</h2>
          <p className="text-slate-300 mb-6">CPD accredited training • Work from home • No experience needed</p>
          <a 
            href="https://enrolment.bookreviewspecialists.app/enroll" 
            className="inline-flex items-center bg-emerald-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-600 transition-colors"
          >
            Enrol Now – From £9.99
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlogInsights;
